import { CANOPY_POST_AUTH_CALLBACK_TARGETS, HANDOFF_NONCE_PARAM } from '@/utils/constants';

/**
 * The address with the nonce removed, or null when the value is not of the permitted shape.
 *
 * Entirely string work, and deliberately so — this mirrors `baseAddressWithoutNonce` in
 * vercel-auth-functions `lib/redirectAllowlist.ts`, which is the same decision for the same
 * reason. The base is sliced from the original and matched byte-exact against a two-item list,
 * so parsing could never change the answer: credentials, a trailing slash, or an uppercase
 * scheme all fail the base match on their own.
 *
 * Parsing also *loosened* it, because `URLSearchParams` normalises. It reads `?%68%6e=A` as the
 * key `hn` and treats a bare trailing `&` or `#` as nothing, so three caller-controlled byte
 * sequences passed an apparently exact check. Raw string checks refuse them.
 *
 * Host case matters here too: a deep link's host picks the app's screen, and
 * `new URL('sleepio://authCallback').host` is `authcallback`, which matches no route. Our list
 * is all kebab-case today, but a mixed-case handoff route added later depends on the match
 * staying byte-exact. Do not normalise this.
 */
const baseAddressWithoutNonce = (trimmed: string): string | null => {
  // Checked on the raw string or not at all: `new URL` reports a bare trailing `#` as no
  // fragment, and a fragment is not part of an address we honour.
  if (trimmed.includes('#')) {
    return null;
  }

  const queryIdx = trimmed.indexOf('?');
  if (queryIdx === -1) {
    return trimmed;
  }

  const query = trimmed.slice(queryIdx + 1);
  // Exactly `hn=<value>`: one key, spelled literally, non-empty, with nothing that could
  // introduce a second parameter.
  if (!new RegExp(`^${HANDOFF_NONCE_PARAM}=[^&#?]+$`).test(query)) {
    return null;
  }

  return trimmed.slice(0, queryIdx);
};

/**
 * Is this exactly one of the addresses a finished Sleepio session may be handed to?
 *
 * Use this — not `isSafeRedirectUri` — anywhere a value decides where a **credential** goes:
 * the address relayed to the auth service, and the address the exchange token is appended to.
 *
 * `isSafeRedirectUri` below checks the scheme only, which is the right check for threading a
 * value through our own navigation but far too loose for delivering a session:
 * `sleepio://anything?x=1` passes it, and `spark://…` passes it too, which would steer a
 * Sleepio session at a different app on the device. This one requires a full match against
 * the two-item list in constants, the same way the auth service does.
 *
 * Structure deliberately mirrors `isAllowedRedirectTarget` in vercel-auth-functions: strip the
 * optional nonce off the raw string, then match what is left byte-exact. Neither repo parses the
 * value — see `baseAddressWithoutNonce` above for why a parse cannot tighten this and does
 * loosen it.
 *
 * The address may carry **at most one non-empty `hn`** — the app's per-flow handoff nonce (see
 * `HANDOFF_NONCE_PARAM`). Everything else is still exact, so the destination stays pinned to the
 * two URIs in the list: the relaxation is the query, not the address. `sleepio://evil?hn=A` and
 * `spark://exchange-callback?hn=A` are refused exactly as before.
 */
export const isAllowedAppHandoffTarget = (uri: string | null | undefined): boolean => {
  if (!uri) {
    return false;
  }
  const trimmed = uri.trim();
  if (!trimmed) {
    return false;
  }
  const base = baseAddressWithoutNonce(trimmed);
  return base !== null && (CANOPY_POST_AUTH_CALLBACK_TARGETS as readonly string[]).includes(base);
};

/**
 * Returns true only for native deep link URIs (e.g. spark://, sleepio://).
 * Uses an allowlist to prevent open redirect attacks.
 *
 * **Scheme-level only.** Fine for threading a redirect target across our own navigation
 * steps, and it is what the Spark flows use. NOT sufficient to decide where a session or a
 * one-time token is delivered — use `isAllowedAppHandoffTarget` above for that.
 */
export const isSafeRedirectUri = (uri: string): boolean => {
  try {
    const url = new URL(uri);
    if (url.username || url.password) {
      return false;
    }
    const allowed = new Set(['spark:', 'sleepio:', 'sleep-companion:']);
    return allowed.has(url.protocol);
  } catch {
    return false;
  }
};

/**
 * Builds "?redirectTo=<encoded>" for a safe deep-link URI, else "".
 * Always validates so it is safe to call with raw URL input. Used to thread
 * the deep-link redirect target across activation step navigations.
 */
export const buildRedirectParam = (redirectTo: string | null | undefined): string => {
  if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
    return '';
  }
  return `?redirectTo=${encodeURIComponent(redirectTo)}`;
};

// Route and _p param are the contract the app registers in AppNavigation's linking config (EmailVerification).
const APP_DEEP_LINK_ROUTES: Record<string, string> = {
  sleepio: 'sleepio://emailVerification',
};

export const buildAppDeepLink = (
  product: string | null | undefined,
  currentUrl: string,
): string | null => {
  const route = product ? APP_DEEP_LINK_ROUTES[product] : undefined;
  if (!route) {
    return null;
  }
  return `${route}?_p=${encodeURIComponent(currentUrl)}`;
};

export type MobileAuthTokens = {
  token: string;
  refreshToken?: string;
  userId?: string;
  userEmail?: string;
};

/**
 * Builds the deep link that hands a completed login to the mobile app.
 * Reconstructs the URL from validated components to break taint flow for CodeQL.
 */
export const buildMobileRedirectUrl = (redirectUri: string, tokens: MobileAuthTokens): string | null => {
  if (!isSafeRedirectUri(redirectUri)) {
    return null;
  }
  const queryParams = new URLSearchParams({
    token: tokens.token,
    ...(tokens.refreshToken && { refresh_token: tokens.refreshToken }),
    ...(tokens.userId && { user_id: tokens.userId }),
    ...(tokens.userEmail && { user_email: tokens.userEmail }),
  });
  const validatedUrl = new URL(redirectUri);
  const safeUrl = `${validatedUrl.protocol}//${validatedUrl.host}${validatedUrl.pathname}`;
  return `${safeUrl}?${queryParams.toString()}`;
};
